import { CloudUploadOutlined, PhotoCameraOutlined } from "@mui/icons-material";
import { Box, Button, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useCallback, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { FileBrowser, type BrowserView } from "../documents/FileBrowser";
import { DEFAULT_FOLDER, folderSlug, templateKey, type FolderId } from "../documents/model";
import { UploadQueue } from "../documents/UploadQueue";
import { useDocumentActions, useDocumentsModel, useUploadQueue, type UploadOptions } from "../documents/useDocuments";
import { useCurrentRoomUser } from "../hooks/useCurrentRoomUser";
import { colors, palette, typeScale } from "../theme/tokens";

import type { PageTemplateProps } from "./types";

// Documents page, explorer layout (2026-09-06, Bill; trimmed the same day).
// Top to bottom:
//
//   1. H1, one sentence on who can see the files, the supplier logo. No
//      Upload button here: the drop area is the one way in (Bill, 2026-09-06),
//      plus "Take a photo" inside it on a phone.
//   2. An outlined drop area: drop or click to browse. New files go to the
//      Uploads folder, or to the folder that is open, and can be moved any
//      time.
//   3. The upload queue, while anything is uploading or failed.
//   4. The file browser (documents/FileBrowser.tsx): folders, then files.
//
// The whole page is also a drop target: dragging a file anywhere shows an
// overlay naming the folder it will land in. The page never scrolls or
// navigates when a folder is opened; the browser swaps its body in place.
//
// Stored rooms opt in with `data.layout = "explorer"` on the uploads page
// (Hush Crib first, 2026-09-06); the canonical default flips once it is signed
// off. Rooms without the flag keep the legacy page (UploadsLegacy.tsx).
export function Documents({ page, room }: PageTemplateProps) {
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down("md"));
    const { user } = useCurrentRoomUser();
    const staff = Boolean(user?.staff);
    const { logoUrl, supplierName } = room.supplier;

    const { model, loading, error } = useDocumentsModel(room);
    const actions = useDocumentActions(room);
    const queue = useUploadQueue(room, model?.loaMarketplaces ?? []);

    const [search, setSearch] = useState("");

    // Deep link (2026-09-08, Bill): `?folder=<id>&file=<key>` opens that folder
    // and highlights the file. The folder the file actually sits in wins over
    // the `folder` parameter (a file may have been moved since the link was
    // copied). The link decides the view only until the reader opens another
    // one; the highlight goes with it.
    const [searchParams] = useSearchParams();
    const linkFile = searchParams.get("file");
    const linkFolder = searchParams.get("folder");
    const linkTarget = useMemo(() => {
        if (!model || (!linkFile && !linkFolder)) return null;
        const holder = linkFile
            ? model.folders.find((f) => f.files.some((x) => x.name === linkFile) || f.templates.some((t) => templateKey(t) === linkFile))
            : undefined;
        // `folder` is the label slug links carry now ("product-data") or a stored id from older links.
        const named = linkFolder
            ? (model.folderById[linkFolder]?.id ?? model.folders.find((f) => folderSlug(f.label) === linkFolder)?.id)
            : undefined;
        const folder = holder?.id ?? named;
        return { folder, file: linkFile };
    }, [model, linkFile, linkFolder]);
    const [chosenView, setChosenView] = useState<BrowserView | null>(null);
    const view: BrowserView = chosenView ?? (linkTarget?.folder ? { kind: "folder", folder: linkTarget.folder } : { kind: "root" });
    const highlight = chosenView ? null : (linkTarget?.file ?? null);
    const setView = (v: BrowserView) => setChosenView(v);

    // Hidden inputs: a normal picker and a camera capture for phones. `target`
    // says where the picked files go.
    const pickerRef = useRef<HTMLInputElement | null>(null);
    const cameraRef = useRef<HTMLInputElement | null>(null);
    const target = useRef<{ folder: FolderId }>({ folder: DEFAULT_FOLDER });

    const optionsFor = useCallback(
        (folder: FolderId): UploadOptions => {
            const def = model?.folderById[folder];
            return { folder, folderLabel: def?.label, taskKey: def?.taskKey };
        },
        [model],
    );

    const addFiles = useCallback(
        (files: Iterable<File>, folder: FolderId) => {
            const list = Array.from(files);
            if (list.length === 0) return;
            queue.enqueue(list, optionsFor(folder));
        },
        [optionsFor, queue],
    );

    const openPicker = (folder: FolderId, camera = false) => {
        target.current = { folder };
        const input = camera ? cameraRef.current : pickerRef.current;
        input?.click();
    };

    // Where a drop or an Upload click lands: the open folder, else Uploads.
    const openFolder = view.kind === "folder" ? model?.folderById[view.folder] : undefined;
    const currentFolder: FolderId = openFolder && !openFolder.readOnly ? openFolder.id : DEFAULT_FOLDER;
    const currentLabel = model?.folderById[currentFolder]?.label ?? "Uploads";

    // ── Drag and drop over the whole page ──
    const [dragOver, setDragOver] = useState(false);
    const dragDepth = useRef(0);
    const onDragEnter = (e: React.DragEvent) => {
        if (!e.dataTransfer?.types?.includes("Files")) return;
        e.preventDefault();
        dragDepth.current += 1;
        setDragOver(true);
    };
    const onDragLeave = () => {
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0) setDragOver(false);
    };
    const onDrop = (e: React.DragEvent) => {
        e.preventDefault();
        dragDepth.current = 0;
        setDragOver(false);
        if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files, currentFolder);
    };

    return (
        <Box
            onDragEnter={onDragEnter}
            onDragOver={(e) => {
                if (e.dataTransfer?.types?.includes("Files")) e.preventDefault();
            }}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            sx={{ position: "relative", px: { xs: 3, md: 4 }, py: { xs: 4, md: 6 }, maxWidth: 1280, minHeight: "60vh" }}
        >
            <input
                ref={pickerRef}
                type="file"
                multiple
                hidden
                onChange={(e) => {
                    if (e.target.files?.length) addFiles(e.target.files, target.current.folder);
                    e.target.value = "";
                }}
            />
            <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                hidden
                onChange={(e) => {
                    if (e.target.files?.length) addFiles(e.target.files, target.current.folder);
                    e.target.value = "";
                }}
            />

            {/* Header */}
            <Box sx={{ mb: 4, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ flex: "1 1 320px", minWidth: 0 }}>
                    <Typography variant="h1" sx={{ ...typeScale.pageTitle, mt: 0, mb: 1 }}>
                        {page.title}
                    </Typography>
                    <Typography variant="body1" sx={{ color: "text.secondary", ...typeScale.lead, maxWidth: 760 }}>
                        Everyone invited to this room and your Bison team can see these files.
                    </Typography>
                </Box>
                {logoUrl && (
                    <Box
                        component="img"
                        src={logoUrl}
                        alt={supplierName}
                        sx={{ height: { xs: 40, md: 56 }, maxWidth: { xs: 140, md: 200 }, objectFit: "contain", flexShrink: 0, mt: 0.5, order: { xs: -1, sm: 0 } }}
                    />
                )}
            </Box>

            <Stack spacing={3}>
                {/* Drop area: outlined, click to browse. */}
                <Box
                    role="button"
                    tabIndex={0}
                    aria-label={`Upload files to ${currentLabel}`}
                    onClick={() => openPicker(currentFolder)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            openPicker(currentFolder);
                        }
                    }}
                    sx={{
                        border: `2px dashed ${colors.line}`,
                        borderRadius: "10px",
                        bgcolor: colors.white,
                        px: 3,
                        py: { xs: 3, md: 4 },
                        textAlign: "center",
                        cursor: "pointer",
                        transition: "border-color 120ms ease, background-color 120ms ease",
                        "&:hover": { borderColor: palette.blue, bgcolor: palette.infoBg },
                        "&:focus-visible": { outline: `2px solid ${palette.blue}`, outlineOffset: 2 },
                    }}
                >
                    <CloudUploadOutlined sx={{ fontSize: 36, color: palette.blue, mb: 0.5 }} />
                    <Typography sx={{ fontWeight: 600, color: colors.navy }}>
                        {mobile ? "Tap to upload files" : "Drop files here, or click to browse"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                        New files go to {currentLabel}. Move them to another folder any time. Up to 50 MB per file.
                    </Typography>
                    {mobile && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PhotoCameraOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                openPicker(currentFolder, true);
                            }}
                            sx={{ textTransform: "none", fontWeight: 600, mt: 1.5 }}
                        >
                            Take a photo
                        </Button>
                    )}
                </Box>

                <UploadQueue items={queue.items} onRetry={queue.retry} onDismiss={queue.dismiss} onClearDone={queue.clearDone} />

                {loading && !model ? (
                    <Typography color="text.secondary">Loading…</Typography>
                ) : error ? (
                    <Typography color="error">Could not load the files. Reload the page to try again.</Typography>
                ) : model ? (
                    <FileBrowser
                        slug={room.slug}
                        model={model}
                        view={view}
                        onView={setView}
                        search={search}
                        highlight={highlight}
                        onSearch={setSearch}
                        staff={staff}
                        onUploadInto={(folder) => openPicker(folder)}
                        onPatch={(name, patch) => actions.patchFile.mutateAsync({ name, patch })}
                        onRemove={(name) => actions.removeFile.mutateAsync(name)}
                        onCreateFolder={(id, label) => actions.createFolder.mutateAsync({ id, label })}
                        busy={actions.patchFile.isPending || actions.removeFile.isPending}
                    />
                ) : null}
            </Stack>

            {dragOver && (
                <Box
                    sx={{
                        position: "absolute",
                        inset: { xs: 8, md: 16 },
                        borderRadius: "10px",
                        border: `2px dashed ${palette.blue}`,
                        bgcolor: "rgba(247, 247, 247, 0.92)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        pointerEvents: "none",
                        zIndex: 5,
                    }}
                >
                    <Box sx={{ textAlign: "center" }}>
                        <CloudUploadOutlined sx={{ fontSize: 48, color: palette.blue }} />
                        <Typography variant="h4" sx={{ fontSize: "1.4rem" }}>
                            Drop to add to {currentLabel}
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
}
